import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { post, get } from '../../api/apiService';
import { validateIndianPhone, errorStyle } from '../../utils/loginUtils';
import image from '../../assets/icons/nayonika-group.png';
import edit from '../../assets/icons/phoneno-edit.png';
import { apiEndPoints } from '../../api/apiEndPoints';
import version from '../../version.json';

const LoginPage: React.FC = () => {
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('phone');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showOtpInput, setShowOtpInput] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(['', '', '', '']);
  const [sessionId, setSessionId] = useState<string>('');
  const [otpSentTo, setOtpSentTo] = useState<string>('');
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [resendTimer, setResendTimer] = useState<number>(30);
  const [userId, setUserId] = useState<string | null>('');
  const navigate = useNavigate();

  const handleLoginMethodChange = (method: 'phone' | 'email') => {
    setLoginMethod(method);
    setShowOtpInput(false);
  };

  const handleRequestOtp = async () => {
    if (loginMethod === 'phone' && phoneNumber) {
      setOtpSentTo(`+91 ${phoneNumber}`);

      if (!validateIndianPhone(phoneNumber)) {
        setErrorMessage('Please enter a valid mobile number');
        return;
      }

      setErrorMessage('');
      setIsSubmitting(true);

      try {
        const resData: any = await post_RequestOtp(phoneNumber);
        if (!resData?.sessionId) {
          throw new Error('Failed to get session ID');
        }
        setSessionId(resData.sessionId);
        setUserId(resData.userId || '');
        setShowOtpInput(true);
        setResendTimer(30);
      } catch (error) {
        console.error('Login error:', error);
        if (error instanceof Error) {
          if (error.message.includes('Network')) {
            setErrorMessage('Network error. Please check your internet connection and try again.');
          } else {
            setErrorMessage(error.message || 'Login failed. Please try again.');
          }
        } else {
          setErrorMessage('An unexpected error occurred');
        }
      } finally {
        setIsSubmitting(false);
      }
    } else if (loginMethod === 'email' && email) {
      setOtpSentTo(email);
      setShowOtpInput(true);
    }
  };

  const post_RequestOtp = async (phoneNumber: string) => {
    const data = {
      applicationCode: 'WEB_PORTAL',
      credential: phoneNumber,
    };
    try {
      const url = apiEndPoints.requestOTPEndPoint;
      const response = await post(url, data);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to request OTP');
    }
  };

  useEffect(() => {
    if (showOtpInput && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [showOtpInput]);

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isNaN(Number(value))) return;

    if (errorMessage) {
      setErrorMessage('');
    }

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value && index < otp.length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleClick = (index: number) => {
    inputRefs.current[index]?.setSelectionRange(1, 1);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const combinedOtp = otp.join("");

    if (combinedOtp.length !== otp.length) {
      setErrorMessage('Please enter complete OTP');
      return;
    }

    try {
      const url = apiEndPoints.verifyOTPEndPoint;
      const data = {
        otp: combinedOtp,
        sessionId: sessionId
      };
      const resData: any = await post(url, data);

      if (!resData || !resData.token) {
        setErrorMessage('Invalid OTP. Please try again.');
        return;
      }

      setErrorMessage('');
      const jwtToken = resData.token;
      const loggedinUser = await get(`${apiEndPoints.userLoginEndPoint}/${userId}`);
      const { firstName, phoneNumber } = loggedinUser;
      localStorage.setItem('loggedinUser', JSON.stringify({ firstName, phoneNumber }));
      localStorage.setItem('jwtToken', jwtToken);
      sessionStorage.removeItem('menuSelected');
      window.location.href = "/LandingPage";
    } catch (error) {
      setErrorMessage('Invalid OTP. Please try again.');
      console.error('OTP verification error:', error);
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (showOtpInput && resendTimer > 0) {
      intervalId = setInterval(() => {
        setResendTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [showOtpInput, resendTimer]);

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    try {
      const data = {
        sessionId: sessionId
      };
      const url = apiEndPoints.reSendOTPEndPoint;
      const resData: any = await post(url, data);
      if (!resData?.sessionId) {
        alert('Error resending OTP');
        return;
      }
      setSessionId(resData.sessionId);
      setShowOtpInput(true);
      setResendTimer(30);
      setOtp(['', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  // Always visible Delete My Account button
  const handleDeleteAccount = () => {
    window.open('/delete-account', '_blank');
  };

  return (
    <div className="flex flex-row bg-white items-center h-screen">
      {/* Logo */}
      <div className="flex items-center text-center justify-center w-[50%] h-full bg-[#F9F7ED] bg-center" style={{ backgroundSize: 'cover', backgroundImage: `url(${image})` }}>
        <div className="flex flex-col items-center justify-center w-full h-full">
          <img
            src="/nayonika_new.png"
            alt="Logo"
            className="w-[244px] h-[192px]"
          />
          {/* Small text on the next line, selectable independently */}
          <span
            className="mt-2 text-md text-black-500 select-text cursor-text bg-transparent"
            style={{
              opacity: 0,
              userSelect: 'text',
            }}
            onMouseDown={e => e.currentTarget.style.opacity = '1'}
            onMouseUp={e => e.currentTarget.style.opacity = '0'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0'}
          >
            <p>Release Version : v-{version.releaseVersion}</p>
            <p>Build Version : {version.buildVersion}</p>
          </span>
        </div>
      </div>

      {/* Login Container */}
      <div className="flex flex-col items-start justify-center w-[50%] h-full pl-12 relative">
        <h1 className="text-3xl font-bold mb-2">Login</h1>
        <p className="text-sm font-normal text-gray-600 mb-4">Enter your details to login</p>

        {/* Login Method Tabs */}
        <div className="flex items-center justify-center mb-6">
          <button text-center
            className={`mr-6 pb-2 text-[#40559C] ${loginMethod === 'phone'
              ? 'border-b-2 border-[#40559C] font-medium text-[#40559C]'
              : 'text-gray-500'}`}
            onClick={() => handleLoginMethodChange('phone')}
          >
            Phone number
          </button>
        </div>

        {/* Form Content */}
        {!showOtpInput ? (
          <div className="mb-8">
            {loginMethod === 'phone' ? (
              <div className="relative">
                <input
                  type="tel"
                  maxLength={10}
                  className="w-full border rounded-full py-3 px-4 pl-12 focus:outline-none focus:ring-1 focus:ring-gray-400"
                  placeholder="Enter mobile number"
                  value={phoneNumber}
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/[^0-9]/g, '');
                    setPhoneNumber(newValue);
                    if (errorMessage || newValue.length === 0) {
                      setErrorMessage('');
                    }
                  }}
                  onFocus={() => {
                    if (errorMessage) {
                      setErrorMessage('');
                    }
                  }}
                />
                <span className="absolute left-4 top-3 text-gray-600">+91</span>
              </div>
            ) : (
              <input
                type="email"
                className="w-full border rounded-full py-3 px-4 focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="Enter email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}

            {errorMessage && <div style={errorStyle}>{errorMessage}</div>}

            <button
              className="w-full bg-black text-white rounded-full py-3 mt-6 hover:bg-gray-800 transition-colors flex items-center justify-center"
              style={{
                cursor: phoneNumber.length < 10 || isSubmitting ? 'not-allowed' : 'pointer',
                backgroundColor: phoneNumber.length === 10 && !isSubmitting ? '#40559C' : '#cccccc',
              }}
              disabled={phoneNumber.length < 10 || isSubmitting}
              onClick={handleRequestOtp}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  Request OTP In Progress
                </>
              ) : (
                'Request OTP'
              )}
            </button>
            <button
              className="w-full bg-red-600 text-white rounded-full py-3 mt-6 transition-colors"

              onClick={handleDeleteAccount}
              type="button"
            >
              Delete My Account
            </button>

          </div>
        ) : (
          <div className="mb-8">
            <p className="text-center mb-4 text-gray-600">
              Enter OTP sent to
              <span className="ml-1 text-[#40559C] font">{otpSentTo}
                <button className="ml-2"
                  onClick={() => { setShowOtpInput(false); setOtp(['', '', '', '']); }}>
                  <img src={`${edit}`}
                    alt="edit"
                    className="w-[16px] h-[16px]"
                  />
                </button>
              </span>
            </p>

            <div className="flex justify-center space-x-4 mb-6">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  autoComplete="off"
                  className="w-12 h-12 text-center border-[1.6px] rounded-[16px] focus:outline-none focus:ring-1 focus:ring-[#40559C]-700 text-lg"
                  value={digit}
                  ref={(input) => { inputRefs.current[index] = input; }}
                  onChange={(e) => handleChange(index, e)}
                  onClick={() => handleClick(index)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                />
              ))}
            </div>

            {errorMessage && <div style={errorStyle}>{errorMessage}</div>}

            <button
              className="w-full bg-[#40559C] text-white rounded-full py-3 mt-3 transition-colors"
              onClick={handleVerifyOtp}
            >
              Verify & Continue
            </button>

            <div className="mt-4">
              <span className="text-gray-600">Didn't receive? </span>
              <button
                className={`${resendTimer > 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-[#40559C] hover:text-[#2a3a7c]'
                  } font-medium`}
                onClick={handleResendOtp}
                disabled={resendTimer > 0}
              >
                Resend OTP
              </button>
              {resendTimer > 0 && <span className="ml-1 text-gray-600">in {resendTimer} seconds</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;